<template>
  <GeneralDialog v-model:visible="visible" :fullscreen="true">
    <div
      class="w-full h-full flex flex-col text-text relative overflow-visible bg-secondary-background"
    >
      <div
        ref="headerRef"
        class="bg-primary relative overflow-visible z-50"
        :style="{ paddingTop: `calc(30px + ${insets.top}px)` }"
      >
        <div class="px-3.75 absolute">
          <button @click="close">
            <IconCustom ligature="arrow_left" size="24" class="text-white" />
          </button>
        </div>

        <div class="flex items-end justify-center overflow-visible">
          <img :src="atsumeLogoIconSvg" alt="logo" class="w-16 h-16" />
          <span class="relative flex pl-3.75 py-1.75 overflow-visible">
            <span class="text-white text-[40px] leading-10">atsu</span>
            <span class="relative text-white text-[40px] leading-10 font-bold"> me </span>
            <span
              class="absolute z-20 -bottom-6.25 right-px text-[12px]/12 font-light text-white overflow-visible"
              >beta</span
            >
          </span>
        </div>
        <svg
          class="absolute -bottom-39 left-0 w-full min-h-40 z-10 pointer-events-none overflow-visible"
          viewBox="0 0 400 60"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M 556 0 L 556 27 Q 391 76 278 41 T -20 29 L -20 0 Z" class="fill-primary" />
        </svg>
      </div>

      <div class="relative">
        <div class="relative px-7.5 pt-22 pb-7.5">
          <div class="inline-flex flex-col items-start">
            <span :class="$typo.openSansBold28">{{ t('patron') }}</span>
            <div class="h-0.75 w-full bg-primary rounded-full mt-2.5"></div>
          </div>

          <p class="mt-7.5 text-text text-balance text-center" :class="$typo.openSans14">
            <template v-for="(part, i) in supportTextParts" :key="i">
              <span v-if="part.bold" class="font-bold">{{ part.text }}</span>
              <template v-else>{{ part.text }}</template>
            </template>
          </p>
        </div>
      </div>

      <DynamicTabView
        :start-tab-configs="tabConfigs"
        :tab-width-map="tabWidthMap"
        :static-tabs="true"
        :flex-height="true"
        :start-key="selectedTabId"
        :select-tab="(key: string) => (selectedPlanId = key)"
        class="pt-3.75 flex-1 min-h-0 rounded-t-[10px] bg-main-background"
        :style="{ paddingBottom: `calc(15px + ${insets.bottom}px)` }"
      >
        <template #tabContent="{ tabConfig }">
          <div
            class="overflow-y-auto h-full w-full p-3.75 pt-7.5 flex flex-col no-scrollbar"
            :style="{ paddingBottom: `calc(${fixedBottomHeight}px - 15px)` }"
          >
            <div class="px-3.75 flex flex-col gap-5">
              <div
                v-for="feature in tabConfig.tabData.features"
                :key="feature.header"
                class="flex items-center gap-5"
              >
                <IconCustom :ligature="feature.icon" size="36" class="text-primary shrink-0" />
                <div class="flex flex-col">
                  <span :class="$typo.openSansBold14" class="leading-7!">{{
                    t(feature.header)
                  }}</span>
                  <span :class="$typo.openSans12" class="text-text-muted text-balance">{{
                    t(feature.description)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </DynamicTabView>

      <div
        ref="fixedBottomRef"
        class="absolute left-0 right-0 bottom-0 z-20 p-3.75"
        :style="{ paddingBottom: `calc(15px + ${insets.bottom}px)` }"
        v-resize-observer="onFixedBottomResize"
      >
        <div
          class="bg-secondary-background rounded-[10px] p-3.75"
          style="box-shadow: 0 0 6px 2px rgba(0, 0, 0, 0.2)"
        >
          <p :class="$typo.openSansBold14" class="text-text text-center mb-3.75">
            {{
              t(selectedPlan.buttonLabel, {
                amount: getProduct(selectedPlan.id)?.priceString ?? '',
              })
            }}
          </p>
          <button
            :class="$typo.openSans14"
            class="w-full bg-primary text-white rounded-[10px] min-h-10 text-center cursor-pointer"
            @click="subscribe(selectedPlan.id)"
          >
            {{ t('becomeSupporterNowButtonLabel') }}
          </button>

          <p class="text-text-muted text-center mt-2.5 text-balance" :class="$typo.openSans12">
            {{ t('subscribeAgreePrefix') }}
            <a
              href="https://atsume.de/agb"
              target="_blank"
              rel="noopener noreferrer"
              class="underline"
              >{{ t('termsOfService') }}</a
            >
            {{ t('subscribeAgreeAnd') }}
            <a
              href="https://atsume.de/data-protection"
              target="_blank"
              rel="noopener noreferrer"
              class="underline"
              >{{ t('privacyPolicy') }}</a
            >{{ t('subscribeAgreeSuffix') }}
          </p>
        </div>
      </div>

      <Transition name="sweep">
        <div
          v-if="purchaseSuccessful"
          class="absolute left-0 right-0 bottom-0 z-40 overflow-visible"
          :style="{ top: headerRef?.offsetHeight + 'px' }"
        >
          <div class="bg-primary w-full h-full relative">
            <Transition name="thank-you">
              <div
                v-if="thankYouVisible"
                class="flex flex-col items-center justify-center h-full px-6 pb-15 cursor-pointer"
                @click="close"
              >
                <IconCustom ligature="heart_plus" size="72" class="text-white mb-6" />
                <span :class="$typo.openSansBold28" class="text-center text-white">{{
                  t('thankYouForSupporting')
                }}</span>
              </div>
            </Transition>
          </div>
          <svg
            class="absolute bottom-1 left-0 w-full pointer-events-none min-h-40"
            viewBox="0 0 400 60"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            style="height: 40px; transform: translateY(100%)"
          >
            <path d="M 556 0 L 556 27 Q 391 76 278 41 T -20 29 L -20 0 Z" class="fill-primary" />
          </svg>
        </div>
      </Transition>

      <canvas
        ref="confettiCanvas"
        class="absolute inset-0 z-50 pointer-events-none w-full h-full"
      />

      <Transition name="fade">
        <div
          v-if="purchaseLoading"
          class="absolute inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <div
            class="h-10 w-10 rounded-full border-3 border-white/30 border-t-white animate-spin"
          />
        </div>
      </Transition>
    </div>
  </GeneralDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import confetti from 'canvas-confetti'
import type { CreateTypes } from 'canvas-confetti'
import { TranslationKey, useLocalizationStore } from '@/stores/localization.ts'
import $typo from '@/styles/typografy.module.css'
import GeneralDialog from '@/components/GeneralDialog.vue'
import DynamicTabView from '@/components/DynamicTabView.vue'
import IconCustom from '@/components/IconCustom.vue'
import { usePaymentStore, type PaymentProduct } from '@/stores/payment.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { showToast } from '@/utils/showToast'
import { storeToRefs } from 'pinia'
import { useInfoStore } from '@/stores/info.ts'
import { sendError } from '@/utils/sendError'
import { IconName } from '@/icons'
import { TabConfig } from '@/utils'
import { vResizeObserver } from '@vueuse/components'
import atsumeLogoIconSvg from '@/assets/icon-no-padding.svg'

const visible = defineModel('visible', { default: false })

const { t } = useLocalizationStore()
const { getProducts, makePurchase } = usePaymentStore()
const products = ref<PaymentProduct[]>()
const purchaseSuccessful = ref(false)
const purchaseLoading = ref(false)
const thankYouVisible = ref(false)
const confettiCanvas = ref<HTMLCanvasElement>()
const headerRef = ref<HTMLDivElement>()
const fixedBottomRef = ref<HTMLDivElement>()
const fixedBottomHeight = ref(0)
let fireConfetti: CreateTypes | undefined
const { customerInfo } = storeToRefs(usePaymentStore())
const { insets, isWeb } = useInfoStore()

watch(visible, async () => {
  selectedTabId.value = selectedPlanId.value
  if (visible.value && !products.value) {
    purchaseLoading.value = true
    try {
      const fetchedProducts = await getProducts()
      products.value = fetchedProducts.products
    } catch (e) {
      sendError(e, { context: 'SubscriptionModal.getProducts' })
      showToast({
        text: t('loadProductsError'),
        duration: 'long',
      })
      visible.value = false
    } finally {
      purchaseLoading.value = false
    }
  }
})

type PlanData = {
  id: string
  titleKey: TranslationKey
  buttonLabel: TranslationKey
  features: {
    header: TranslationKey
    description: TranslationKey
    icon: IconName
  }[]
}

const baseBenefits = [
  {
    header: 'noPriceIncreaseHeader',
    description: 'noPriceIncreaseDescription',
    icon: 'badge_euro',
  },
  {
    header: 'noAdsHeader',
    description: 'noAdsDescription',
    icon: 'megaphone_off',
  },
  {
    header: 'discordRoleHeader',
    description: 'discordRoleDescription',
    icon: 'user_star',
  },
  {
    header: 'supportDevelopmentHeader',
    description: 'supportDevelopmentDescription',
    icon: 'heart_plus',
  },
]

const yearlyBenefits = [
  {
    header: 'saveMoneyHeader',
    description: 'saveMoneyDescription',
    icon: 'piggy_bank',
  },
  ...baseBenefits,
]

const planData = computed(
  () =>
    [
      ...(isWeb
        ? [
            {
              id: 'supporter_one_year_manual',
              titleKey: 'oneTimeYearly',
              buttonLabel: 'billedAsYearlyOneTime',
              features: [
                {
                  header: 'noAdsHeader',
                  description: 'noAdsDescription',
                  icon: 'megaphone_off',
                },
                {
                  header: 'discordRoleHeader',
                  description: 'discordRoleDescription',
                  icon: 'user_star',
                },
                {
                  header: 'supportDevelopmentHeader',
                  description: 'supportDevelopmentDescription',
                  icon: 'heart_plus',
                },
              ],
            },
          ]
        : []),
      {
        id: '$rc_annual',
        titleKey: 'yearly',
        buttonLabel: 'billedAsYearly',
        features: yearlyBenefits,
      },
      {
        id: '$rc_monthly',
        titleKey: 'monthly',
        buttonLabel: 'billedAsMonthly',
        features: baseBenefits,
      },
    ] as PlanData[],
)

const selectedPlanId = ref<string>(planData.value[0].id)
const selectedTabId = ref<string>(planData.value[0].id)
const selectedPlan = computed(
  () => planData.value.find((p) => p.id === selectedPlanId.value) ?? planData.value[0],
)

const tabConfigs = computed(
  () =>
    planData.value.map((plan) => ({
      key: plan.id,
      title: t(plan.titleKey),
      tabData: plan,
      widthKey: 'a',
    })) as TabConfig<PlanData>[],
)

const tabWidthMap = { a: 120 }

const supportTextParts = computed(() => {
  const text = t('youWantToSupportAtsume')
  const idx = text.toLowerCase().indexOf('atsume')
  if (idx === -1) return [{ text, bold: false }]
  return [
    { text: text.slice(0, idx), bold: false },
    { text: text.slice(idx, idx + 6), bold: true },
    { text: text.slice(idx + 6), bold: false },
  ]
})

function getProduct(id: string): PaymentProduct | undefined {
  return products.value?.find((p) => p.id === id)
}

const activeProduct = computed(() => {
  return products.value?.find((p) => customerInfo.value?.activeSubscriptions.includes(p.identifier))
})

function onFixedBottomResize(entries: readonly ResizeObserverEntry[]) {
  fixedBottomHeight.value = entries[0]?.borderBoxSize?.[0]?.blockSize ?? 0
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

function close() {
  visible.value = false
  purchaseSuccessful.value = false
  thankYouVisible.value = false
}

async function subscribe(planId: string) {
  const product = getProduct(planId)
  if (!product) return
  purchaseLoading.value = true
  try {
    if (isWeb) {
      visible.value = false
    }
    await makePurchase(product, activeProduct.value)
    if (isWeb) {
      visible.value = true
    }
    const { authState } = storeToRefs(useAuthStore())
    if (authState.value.userAdmin) {
      authState.value.userAdmin = {
        ...authState.value.userAdmin,
        premiumTier: product.identifier,
      }
    }
    purchaseSuccessful.value = true
    purchaseLoading.value = false
    await delay(1300)
    thankYouVisible.value = true
    await delay(50)
    if (confettiCanvas.value) {
      fireConfetti ??= confetti.create(confettiCanvas.value, { resize: true })
      const count = 200
      const defaults = {
        origin: { y: 0.6 },
      }

      function fire(particleRatio: number, opts: confetti.Options) {
        fireConfetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio),
        })
      }

      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      })
      fire(0.2, {
        spread: 60,
      })
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      })
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      })
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      })
    }
  } catch (e) {
    if (isWeb) {
      visible.value = true
    }
    const message = e instanceof Error ? e.message : 'Unknown error'
    if (!message.includes('cancelled')) {
      showToast({
        text: t('purchaseError', { error: message }),
        duration: 'long',
      })
    }
  } finally {
    purchaseLoading.value = false
  }
}
</script>

<style scoped>
.sweep-enter-active {
  transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.sweep-enter-from {
  transform: translateY(-100%);
}

.sweep-enter-to {
  transform: translateY(0);
}

.thank-you-enter-active {
  transition:
    opacity 0.3s ease,
    transform 0.5s ease;
}

.thank-you-enter-from {
  opacity: 0;
}

.thank-you-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.no-scrollbar {
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
